import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  createInvoice,
  deleteInvoice,
  editInvoice,
  getInvoiceById,
  getInvoicePdfPreview,
  getInvoices,
  sendInvoiceEmail,
  updateInvoiceStatus,
} from "./thunk";
import { InvoiceDetailsPayload, InvoicePayloadData } from "types/invoice.api";
import {
  ApiResponse,
  InvoiceSummary,
  PaginatedInvoiceSummaryPayload,
  PaginationMeta,
} from "types/ApiResponse";
import { StatusPayload } from "types/quote.api";
import { deleteAttachment } from "slices/quotes/thunk";

type RequestStatus = "idle" | "loading" | "succeeded" | "failed";

interface InvoiceStatus {
  fetch: RequestStatus;
  fetchById: RequestStatus;
  add: RequestStatus;
  edit: RequestStatus;
  delete: RequestStatus;
  updateInvoiceStatus: RequestStatus;
  sendEmail: RequestStatus;
  pdfPreview: RequestStatus;
}

interface InvoiceErrors {
  fetch: string | null;
  fetchById: string | null;
  add: string | null;
  edit: string | null;
  delete: string | null;
  updateInvoiceStatus: string | null;
  sendEmail: string | null;
  pdfPreview: string | null;
}

export interface InvoiceState {
  invoices: InvoicePayloadData[];
  ids: number[];
  invoiceDetails: InvoiceDetailsPayload | null;
  summary: InvoiceSummary | null;
  pagination: PaginationMeta;
  status: InvoiceStatus;
  errors: InvoiceErrors;
  pdfPreviewHtml: string | null;
}

export const initialState: InvoiceState = {
  invoices: [],
  ids: [],
  summary: null,
  invoiceDetails: null,
  pdfPreviewHtml: null,
  pagination: {
    current_page: 0,
    from: 0,
    last_page: 0,
    links: [],
    path: "",
    per_page: 0,
    to: 0,
    total: 0,
  },

  status: {
    fetch: "idle",
    fetchById: "idle",
    add: "idle",
    edit: "idle",
    delete: "idle",
    updateInvoiceStatus: "idle",
    sendEmail: "idle",
    pdfPreview: "idle",
  },

  errors: {
    fetch: null,
    fetchById: null,
    add: null,
    edit: null,
    delete: null,
    updateInvoiceStatus: null,
    sendEmail: null,
    pdfPreview: null,
  },
};

const invoiceSlice = createSlice({
  name: "invoice",
  initialState,
  reducers: {
    resetCreateInvoiceStatus(state) {
      state.status.add = "idle";
    },
    resetEditInvoiceStatus(state) {
      state.status.edit = "idle";
    },
    clearInvoiceDetails: (state) => {
      state.invoiceDetails = null;
    },
    clearInvoicePdfPreview: (state) => {
      state.pdfPreviewHtml = null;
      state.status.pdfPreview = "idle";
      state.errors.pdfPreview = null;
    },
  },
  extraReducers: (builder) => {
    // get Invoices
    builder.addCase(getInvoices.pending, (state) => {
      state.status.fetch = "loading";
      state.errors.fetch = null;
    });
    builder.addCase(
      getInvoices.fulfilled,
      (
        state,
        action: PayloadAction<
          ApiResponse<PaginatedInvoiceSummaryPayload<InvoicePayloadData>>
        >
      ) => {
        state.status.fetch = "succeeded";
        const responsePayload = action.payload?.payload;
        state.invoices = responsePayload?.data || [];
        state.ids = (responsePayload?.data || [])?.map((invoice) => invoice.id);
        state.pagination = responsePayload?.meta || initialState.pagination;
        state.summary = responsePayload?.summary || null;
      }
    );
    builder.addCase(getInvoices.rejected, (state, action) => {
      state.status.fetch = "failed";
      state.errors.fetch = action.payload || null;
    });

    // get Invoice by ID
    builder.addCase(getInvoiceById.pending, (state) => {
      state.status.fetchById = "loading";
      state.errors.fetchById = null;
    });
    builder.addCase(
      getInvoiceById.fulfilled,
      (state, action: PayloadAction<ApiResponse<InvoiceDetailsPayload>>) => {
        state.status.fetchById = "succeeded";
        const invoice = action.payload.payload;
        state.invoiceDetails = invoice;
      }
    );
    builder.addCase(getInvoiceById.rejected, (state, action) => {
      state.status.fetchById = "failed";
      state.errors.fetchById = action.payload || null;
    });

    //create invoice
    builder.addCase(createInvoice.pending, (state) => {
      state.status.add = "loading";
      state.errors.add = null;
    });
    builder.addCase(
      createInvoice.fulfilled,
      (state, action: PayloadAction<ApiResponse<InvoiceDetailsPayload>>) => {
        state.status.add = "succeeded";
        let data = action.payload.payload;
        state.invoiceDetails = data;
        // TODO
        state.pagination.to++;
        state.pagination.total++;
        state.pagination.last_page = Math.ceil(
          state.pagination.total / state.pagination.per_page
        );
      }
    );
    builder.addCase(createInvoice.rejected, (state, action) => {
      state.status.add = "failed";
      state.errors.add = action.payload || null;
    });

    //Edit invoice
    builder.addCase(editInvoice.pending, (state) => {
      state.status.edit = "loading";
      state.errors.edit = null;
    });
    builder.addCase(editInvoice.fulfilled, (state, action) => {
      state.status.edit = "succeeded";
      const data = action.payload.payload;
      state.invoiceDetails = data;
    });
    builder.addCase(editInvoice.rejected, (state, action) => {
      state.status.edit = "failed";
      state.errors.edit = action.payload || null;
    });

    // delete Invoice
    builder.addCase(deleteInvoice.pending, (state) => {
      state.status.delete = "loading";
      state.errors.delete = null;
    });
    builder.addCase(
      deleteInvoice.fulfilled,
      (state, action: PayloadAction<ApiResponse<number>>) => {
        state.status.delete = "succeeded";
        const deletedInvoiceId = action.payload.payload;
        state.invoices = state.invoices.filter(
          (invoice) => invoice.id !== deletedInvoiceId
        );
        state.ids = state.ids.filter((id) => id !== deletedInvoiceId);
        state.pagination.to--;
        state.pagination.total--;
        state.pagination.last_page = Math.ceil(
          state.pagination.total / state.pagination.per_page
        );
      }
    );
    builder.addCase(deleteInvoice.rejected, (state, action) => {
      state.status.delete = "failed";
      state.errors.delete = action.payload || null;
    });

    builder.addCase(updateInvoiceStatus.pending, (state) => {
      state.status.updateInvoiceStatus = "loading";
      state.errors.updateInvoiceStatus = null;
    });

    builder.addCase(
      updateInvoiceStatus.fulfilled,
      (state, action: PayloadAction<ApiResponse<StatusPayload>>) => {
        state.status.updateInvoiceStatus = "succeeded";
        const statusPayload = action.payload.payload;
        state.invoices = state.invoices.map((e) => {
          if (e.id === statusPayload.invoice_id) {
            return { ...e, status: statusPayload.status };
          } else {
            return e;
          }
        });
        if (
          state.invoiceDetails &&
          state.invoiceDetails.id === statusPayload.invoice_id
        ) {
          state.invoiceDetails.status = statusPayload.status;
        }
      }
    );
    builder.addCase(updateInvoiceStatus.rejected, (state, action) => {
      state.status.updateInvoiceStatus = "failed";
      state.errors.updateInvoiceStatus = action.payload || null;
    });

    // delete attachment by quote id
    builder.addCase(
      deleteAttachment.fulfilled,
      (state, action: PayloadAction<ApiResponse<number>>) => {
        if (state.invoiceDetails?.attachments?.length) {
          const deletedId = action.payload.payload;

          state.invoiceDetails.attachments =
            state.invoiceDetails.attachments.filter(
              (attachment) => attachment.id !== deletedId
            );
        }
      }
    );

    //Send Invoice Email
    builder.addCase(sendInvoiceEmail.pending, (state) => {
      state.status.sendEmail = "loading";
      state.errors.sendEmail = null;
    });
    builder.addCase(sendInvoiceEmail.fulfilled, (state, action) => {
      state.status.sendEmail = "succeeded";
    });
    builder.addCase(sendInvoiceEmail.rejected, (state, action) => {
      state.status.sendEmail = "failed";
      state.errors.sendEmail = action.payload || null;
    });

    // get Invoice Pdf Preview
    builder.addCase(getInvoicePdfPreview.pending, (state) => {
      state.status.pdfPreview = "loading";
      state.errors.pdfPreview = null;
    });
    builder.addCase(
      getInvoicePdfPreview.fulfilled,
      (state, action: PayloadAction<string>) => {
        state.status.pdfPreview = "succeeded";
        state.pdfPreviewHtml = action.payload;
      }
    );
    builder.addCase(getInvoicePdfPreview.rejected, (state, action) => {
      state.status.pdfPreview = "failed";
      state.errors.pdfPreview = action.payload || null;
    });
  },
});

export default invoiceSlice.reducer;

export const {
  resetCreateInvoiceStatus,
  resetEditInvoiceStatus,
  clearInvoiceDetails,
  clearInvoicePdfPreview,
} = invoiceSlice.actions;

export const selectInvoicesState = (state: any) =>
  state.invoice as typeof initialState;
